import React, { useEffect, useState, createElement } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { StoryProvider } from './components/StoryContext';
import { Navigation } from './components/Navigation';
import { HomePage } from './components/HomePage';
import { StoryEbookWrapper } from './components/StoryEbookWrapper';
import { Library } from './components/Library';
import { StoryGarden } from './components/StoryGarden';
import { ParentDashboard } from './components/auth/ParentDashboard';
import { EasyCreate } from './components/EasyCreate';
import { PricingPage } from './components/PricingPage';
import { CreateAccount } from './pages/CreateAccount';
import { ParentLogin } from './pages/ParentLogin';
import { FaqPage } from './pages/FaqPage';
import { LockScreen } from './pages/LockScreen';
import { Footer } from './components/Footer';
import { CreatorMode } from './pages/CreatorMode';
import { StoryViewer } from './components/StoryViewer';
import { StoryIdeasTips } from './pages/StoryIdeasTips';
import { ErrorBoundary } from './components/ErrorBoundary';
import { trackPageView, initializeTracking, isTrackingInitialized } from './utils/tracking';
type ChildAccount = {
  id: string;
  name: string;
  avatar: string;
  age: number;
  readingLevel: 'beginner' | 'intermediate' | 'advanced';
};
// Create a wrapper component for Library to handle navigation
const LibraryWrapper = () => {
  const navigate = useNavigate();
  return <Library onBack={() => navigate('/')} data-id="element-0" />;
};
export function App() {
  const [currentChild, setCurrentChild] = useState<ChildAccount>({
    id: '1',
    name: 'Emma',
    avatar: '🦄',
    age: 7,
    readingLevel: 'intermediate'
  });
  const [isTrackingReady, setIsTrackingReady] = useState(false);
  // Add GA script tag dynamically
  useEffect(() => {
    const addGAScript = () => {
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://www.googletagmanager.com/gtag/js?id=G-M50P4LPNWG';
      script.onerror = () => console.error('Failed to load Google Analytics');
      document.head.appendChild(script);
      // Add the gtag initialization code
      const initScript = document.createElement('script');
      initScript.textContent = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
      `;
      document.head.appendChild(initScript);
    };
    if (!document.querySelector('script[src*="googletagmanager.com"]')) {
      addGAScript();
    }
  }, []);
  // Initialize tracking once GA script is loaded
  useEffect(() => {
    const initialize = () => {
      try {
        const isInitialized = initializeTracking();
        setIsTrackingReady(isInitialized);
        if (!isInitialized) {
          const checkInterval = setInterval(() => {
            if (isTrackingInitialized()) {
              setIsTrackingReady(true);
              clearInterval(checkInterval);
            }
          }, 1000);
          // Cleanup interval after 10 seconds maximum
          setTimeout(() => clearInterval(checkInterval), 10000);
          return () => clearInterval(checkInterval);
        }
      } catch (error) {
        console.error('Error initializing tracking:', error);
        setIsTrackingReady(false);
      }
    };
    // Delay initialization slightly to ensure script has time to load
    const initTimer = setTimeout(initialize, 1000);
    return () => clearTimeout(initTimer);
  }, []);
  // Track page views only after tracking is initialized
  useEffect(() => {
    if (!isTrackingReady) return;
    const handleRouteChange = () => {
      trackPageView(window.location.pathname);
    };
    // Track initial page view
    handleRouteChange();
    // Listen for route changes
    window.addEventListener('popstate', handleRouteChange);
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, [isTrackingReady]);
  return <ErrorBoundary data-id="element-1">
      <HelmetProvider data-id="element-2">
        <Router data-id="element-3">
          <StoryProvider data-id="element-4">
            <div className="min-h-screen flex flex-col bg-purple-50" data-id="element-5">
              <Navigation data-id="element-6" />
              <Routes data-id="element-7">
                <Route path="/" element={<HomePage data-id="element-9" />} data-id="element-8" />
                <Route path="/lock-screen" element={<LockScreen data-id="element-11" />} data-id="element-10" />
                <Route path="/library" element={<LibraryWrapper data-id="element-13" />} data-id="element-12" />
                <Route path="/garden" element={<StoryGarden data-id="element-15" />} data-id="element-14" />
                <Route path="/story-ideas-tips" element={<StoryIdeasTips data-id="element-17" />} data-id="element-16" />
                <Route path="/dashboard" element={<ParentDashboard onBack={() => <Navigate to="/" data-id="element-20" />} onChildSelect={child => setCurrentChild(child)} data-id="element-19" />} data-id="element-18" />
                <Route path="/easy-create" element={<EasyCreate data-id="element-22" />} data-id="element-21" />
                <Route path="/pricing" element={<PricingPage data-id="element-24" />} data-id="element-23" />
                <Route path="/create-account" element={<CreateAccount data-id="element-26" />} data-id="element-25" />
                <Route path="/login" element={<ParentLogin data-id="element-28" />} data-id="element-27" />
                <Route path="/faq" element={<FaqPage data-id="element-30" />} data-id="element-29" />
                <Route path="/creator" element={<CreatorMode data-id="element-32" />} data-id="element-31" />
                <Route path="/story" element={<StoryViewer data-id="element-34" />} data-id="element-33" />
              </Routes>
              <StoryEbookWrapper data-id="element-35" />
              <Footer data-id="element-36" />
            </div>
          </StoryProvider>
        </Router>
      </HelmetProvider>
    </ErrorBoundary>;
}